import React, { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import Loader from '../components/Loader/Loader';
import { useAuth } from '../context/AuthContext';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';

const Failure = lazy(() => import('../RedirectPage/failure'));
const Success = lazy(() => import('../RedirectPage/success'));
const Login = lazy(() => import('../pages/Login'));
const Home = lazy(() => import('../features/employee/Home'));
const Profile = lazy(() => import('../pages/Profile'));
const BloodGroupPage = lazy(() => import('../pages/BloodGroupPage'));
const LeaveApplication = lazy(() => import('../pages/LeaveApplication'));
const LeaveReview = lazy(() => import('../pages/LeaveReview'));
const TicketManagement = lazy(() => import('../pages/TicketManagement'));
const AddTicket = lazy(() => import('../pages/AddTicket'));
const TicketReview = lazy(() => import('../pages/TicketReview'));
const ApplyForHolidayLeave = lazy(() => import('../pages/ApplyHolidayLeave'));
const AddHoliday = lazy(() => import('../pages/AddHoliday'));
const TeamMember = lazy(() => import('../pages/TeamMember'));
const EditTicket = lazy(() => import('../pages/EditTicket'));

const TeamManagement = lazy(() => import('../pages/TeamManagement'));
const EmployeeManagement = lazy(() => import('../pages/EmployeeManagement'));
const LeaveManagement = lazy(() => import('../pages/LeaveManagement'));
const LeaveReport = lazy(() => import('../pages/LeaveReport'));
const UserLeaveReport = lazy(() => import('../pages/UserLeaveReport'));
const AddUser = lazy(() => import('../features/employee/AddUser'));
const EditUser = lazy(() => import('../features/employee/EditUser'));
const TeamEdit = lazy(() => import('../pages/TeamEdit'));
const TeamAdd = lazy(() => import('../pages/TeamAdd'));
const DesignationEdit = lazy(() => import('../pages/DesignationEdit'));
const DesignationAdd = lazy(() => import('../pages/DesignationAdd'));
const Assets = lazy(() => import('../pages/Assets'));
const AddInvoice = lazy(() => import('../pages/AddInvoice'));
const EditInvoice = lazy(() => import('../pages/EditInvoice'));
const LeaveAdjust = lazy(() => import('../pages/LeaveAdjust'));
const AddStaticEmail = lazy(() => import('../pages/AddStaticEmail'));
const HolidayLeaveManagement = lazy(() => import('../pages/HolidayLeaveManagement'));
const ApplicationReview = lazy(() => import('../pages/ApplicationReview'));

const PageFallback = () => <Loader show label="Loading page" />;

const AllRoutes = () => {
  const { currentUser } = useAuth();
  const role = currentUser?.userRole?.toLowerCase();

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path='/success/:token' element={<Success />} />
        <Route path='/failure' element={<Failure />} />
        <Route path={'/*'} element={<PublicRoute />}>
          <Route path={''} element={<Login />} />
        </Route>
        <Route path={'/*'} element={<PrivateRoute />}>
          <Route path={'dashboard'} element={<Home />} />
          <Route path='add-holiday' element={<AddHoliday />} />
          <Route path='apply-holiday-leave' element={<ApplyForHolidayLeave />} />
          {role === 'admin' && (
            <>
              <Route path={'team-management'} element={<TeamManagement />} />
              <Route path={'employee-management'} element={<EmployeeManagement />} />
              <Route path={'leave-management'} element={<LeaveManagement />} />
              <Route path='leave-report' element={<LeaveReport />} />
              <Route path='leave-report/:slug' element={<UserLeaveReport />} />
              <Route path='addUser' element={<AddUser />}>
                <Route path=':slug' element={<AddUser />} />
              </Route>
              <Route path='edit-user/:slug' element={<EditUser />} />
              <Route path='team-edit' element={<TeamEdit />}>
                <Route path=':slug' element={<TeamEdit />} />
              </Route>
              <Route path='team-add' element={<TeamAdd />} />
              <Route path='designation-edit' element={<DesignationEdit />}>
                <Route path=':slug' element={<DesignationEdit />} />
              </Route>
              <Route path='designation-add' element={<DesignationAdd />} />
              <Route path='assets' element={<Assets />} />
              <Route path='add-invoice' element={<AddInvoice />} />
              <Route path='edit-invoice/:slug' element={<EditInvoice />} />
              <Route path='leave-adjust' element={<LeaveAdjust />}>
                <Route path=':slug' element={<LeaveAdjust />} />
              </Route>
              <Route path='add-static-email' element={<AddStaticEmail />} />
              <Route path='holiday-leave-application' element={<HolidayLeaveManagement />} />
              <Route path='application-review/:slug' element={<ApplicationReview />} />
            </>
          )}
          {(role === 'supervisor' || role === 'admin') && (
            <Route path='edit-ticket/:slug' element={<EditTicket />} />
          )}
          {role === 'supervisor' && (
            <Route path={'team-member'} element={<TeamMember />} />
          )}
          <Route path='leave-review' element={<LeaveReview />}>
            <Route path=':slug' element={<LeaveReview />} />
          </Route>
          <Route path='ticket-review/:slug' element={<TicketReview />} />
          <Route path='profile' element={<Profile />} />
          {/* Any logged-in user, regardless of role — matches the
              checkValidation guard on GET /emp/blood-groups. */}
          <Route path='blood-group' element={<BloodGroupPage />} />
          <Route path='leave-application' element={<LeaveApplication />} />
          <Route path={'ticket-management'} element={<TicketManagement />} />
          <Route path={'add-ticket'} element={<AddTicket />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AllRoutes;
